<script setup lang="ts">
import DancingDotsLoader from '@/components/DancingDotsLoader.vue'
import type { BillingNoticeProps } from '#shared/types/billingNotice'

const props = defineProps<BillingNoticeProps>()

const { t } = useI18n()
const { formatCurrency } = useCurrency()
const { formatDate } = useDate()

const { currentPricePlan } = storeToRefs(useBusinessStore())
const { currency, seatPrice, isYearlyPlan } = useSeatPricing(() => props.businessId)

const proratedLineText = computed(() => {
  const params = { count: props.newlyPurchased, price: formatCurrency(seatPrice.value, currency.value) }
  return isYearlyPlan.value
    ? t('business.members.billing_notice.prorated_yearly', params, props.newlyPurchased)
    : t('business.members.billing_notice.prorated', params, props.newlyPurchased)
})

const nextPeriodPriceText = computed(() => formatCurrency(props.nextPeriodSeatTotal * seatPrice.value, currency.value))

const activeLineCount = computed(
  () => (props.fromAvailable > 0 ? 1 : 0) + (props.fromPending > 0 ? 1 : 0) + (props.newlyPurchased > 0 ? 1 : 0)
)
const hasBreakdown = computed(() => activeLineCount.value > 0)
const showBullets = computed(() => activeLineCount.value > 1)

const onlyAvailable = computed(() => props.fromAvailable > 0 && props.fromPending === 0 && props.newlyPurchased === 0)

const nextPeriodChanged = computed(() => props.fromPending > 0 || props.newlyPurchased > 0)

const renewalDate = computed(() => {
  const endAt = currentPricePlan.value?.endAt
  return endAt ? formatDate(new Date(endAt)) : ''
})

const { payNowAmount, isLoading: isLoadingPrice } = useProratedSeatPrice(
  () => props.businessId,
  () => props.currentSeatTotal,
  () => props.newlyPurchased
)

const payNowDisplay = computed(() =>
  payNowAmount.value === null ? '' : formatCurrency(payNowAmount.value, currency.value)
)
</script>

<template>
  <div v-if="hasBreakdown" class="billing-notice">
    <div v-if="onlyAvailable" class="billing-notice__compact">
      <p class="billing-notice__compact-title">
        <i18n-t keypath="business.members.billing_notice.available_no_cost" tag="span" :plural="fromAvailable">
          <template #emphasis>
            <span class="billing-notice__compact-emphasis">
              {{
                t('business.members.billing_notice.available_no_cost_emphasis', { count: fromAvailable }, fromAvailable)
              }}
            </span>
          </template>
        </i18n-t>
      </p>
      <p class="billing-notice__compact-hint">
        {{ t('business.members.billing_notice.available_no_cost_hint', fromAvailable) }}
      </p>
    </div>

    <template v-else>
      <div class="billing-notice__section">
        <p class="billing-notice__heading">{{ t('business.members.billing_notice.current_period') }}</p>
        <ul class="billing-notice__list">
          <li v-if="fromAvailable > 0" class="billing-notice__item">
            <span v-if="showBullets" class="billing-notice__bullet">•</span>
            <span>
              {{
                t(
                  'business.members.billing_notice.available',
                  { count: fromAvailable, total: totalAvailable },
                  fromAvailable
                )
              }}
              <span class="billing-notice__muted">{{ t('business.members.billing_notice.no_charge') }}</span>
            </span>
          </li>
          <li v-if="fromPending > 0" class="billing-notice__item">
            <span v-if="showBullets" class="billing-notice__bullet">•</span>
            <span>
              {{ t('business.members.billing_notice.restored', { count: fromPending }, fromPending) }}
              <span class="billing-notice__muted">{{ t('business.members.billing_notice.no_charge') }}</span>
            </span>
          </li>
          <li v-if="newlyPurchased > 0" class="billing-notice__item">
            <span v-if="showBullets" class="billing-notice__bullet">•</span>
            <span>{{ proratedLineText }}</span>
          </li>
        </ul>
        <p v-if="newlyPurchased > 0 && (isLoadingPrice || payNowAmount !== null)" class="billing-notice__pay-now">
          <dancing-dots-loader v-if="isLoadingPrice" />
          <i18n-t v-else-if="payNowAmount !== null" keypath="business.members.billing_notice.pay_now" tag="span">
            <template #amount>
              <strong>{{ payNowDisplay }}</strong>
            </template>
          </i18n-t>
        </p>
      </div>

      <div v-if="nextPeriodChanged" class="billing-notice__section billing-notice__section--next">
        <p class="billing-notice__next-intro">
          {{ t('business.members.billing_notice.next_period_intro', { date: renewalDate }) }}
        </p>
        <p class="billing-notice__next-total">
          <i18n-t
            v-if="isYearlyPlan"
            keypath="business.members.billing_notice.next_period_total_yearly"
            tag="span"
            :plural="nextPeriodSeatTotal"
          >
            <template #count>
              <strong>{{ nextPeriodSeatTotal }}</strong>
            </template>
            <template #price>{{ nextPeriodPriceText }}</template>
          </i18n-t>
          <i18n-t
            v-else
            keypath="business.members.billing_notice.next_period_total"
            tag="span"
            :plural="nextPeriodSeatTotal"
          >
            <template #count>
              <strong>{{ nextPeriodSeatTotal }}</strong>
            </template>
            <template #price>{{ nextPeriodPriceText }}</template>
          </i18n-t>
        </p>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.billing-notice {
  display: flex;
  flex-direction: column;
  border: 1px solid $c-grey-bg-5;
  border-radius: 10px;
  color: $c-skribbleu;
  font-size: 15px;
  line-height: 22px;
}

.billing-notice__compact {
  padding: 7 * $bw;
}

.billing-notice__compact-title {
  font-size: 16px;
}

.billing-notice__compact-emphasis {
  color: $c-seat-available;
}

.billing-notice__compact-hint {
  margin-top: 2 * $bw;
  font-size: 13px;
  color: $c-grey-dark;
}

.billing-notice__section {
  padding: 5 * $bw 7 * $bw 7 * $bw;

  & + .billing-notice__section {
    border-top: 1px solid $c-grey-bg-5;
  }
}

.billing-notice__section--next {
  background: $c-grey-bg-8;
  border-radius: 0 0 10px 10px;
}

.billing-notice__heading {
  margin-bottom: 4 * $bw;
  font-size: 14px;
  line-height: 26px;
  color: $c-seat-available;
}

.billing-notice__list {
  display: flex;
  flex-direction: column;
  gap: 3 * $bw;
  margin: 0;
  padding: 0;
  list-style: none;
}

.billing-notice__item {
  display: flex;
  gap: 2 * $bw;
}

.billing-notice__bullet {
  flex-shrink: 0;
  color: $c-grey-dark;
}

.billing-notice__muted {
  color: $c-grey;
}

.billing-notice__pay-now {
  margin-top: 5 * $bw;
  text-decoration: underline;
}

.billing-notice__next-intro {
  margin-bottom: 4 * $bw;
  font-size: 14px;
  color: $c-grey-dark;
}

.billing-notice__next-total {
  font-size: 16px;
}
</style>
